// pages/CompetencyQuiz/QuestionsTab.js
//
// The question bank (competencyQuiz.manage: IT Admin / IT Team). Search, add, edit,
// retire / restore. Retiring keeps the question on old attempts but stops it being
// drawn (e.g. its SOP was revised). Old results keep the answer key they were scored
// with (CorrectKeys snapshot), so editing a question never rewrites history.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge, Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table,
} from "reactstrap";
import { toast } from "react-toastify";
import useBackCloses from "../../hooks/useBackCloses";
import { EmptyState, useDebouncedValue } from "../../Components/Common/DashboardKit";
import { createQuestion, errorText, listQuestions, pick, updateQuestion } from "../../services/competencyQuizApi";
import useSingleFlight from "./useSingleFlight";

const KEYS = ["A", "B", "C", "D"];
const ROLE_CHOICES = ["Common", "Design", "Manufacturing", "Applications"];

const blank = () => ({
    questionId: null, questionCode: "", domain: "", roles: "Common", questionType: "single", questionText: "",
    rationale: "", sourceDocument: "", sourceSection: "", difficulty: "Intermediate", isCritical: false,
    referenceUrl: "", isActive: true, options: KEYS.map((k) => ({ key: k, text: "" })), correctKeys: [],
});

const fromApi = (q) => ({
    questionId:     pick(q, "QuestionId", "questionId"),
    questionCode:   pick(q, "QuestionCode", "questionCode") || "",
    domain:         pick(q, "Domain", "domain") || "",
    roles:          pick(q, "Roles", "roles") || "",
    questionType:   pick(q, "QuestionType", "questionType") || "single",
    questionText:   pick(q, "QuestionText", "questionText") || "",
    rationale:      pick(q, "Rationale", "rationale") || "",
    sourceDocument: pick(q, "SourceDocument", "sourceDocument") || "",
    sourceSection:  pick(q, "SourceSection", "sourceSection") || "",
    difficulty:     pick(q, "Difficulty", "difficulty") || "",
    isCritical:     !!pick(q, "IsCritical", "isCritical"),
    referenceUrl:   pick(q, "ReferenceUrl", "referenceUrl") || "",
    isActive:       pick(q, "IsActive", "isActive") !== false,
    options:        KEYS.map((k) => {
        const o = (pick(q, "Options", "options") || []).find((x) => pick(x, "Key", "key") === k);
        return { key: k, text: o ? pick(o, "Text", "text") : "" };
    }),
    correctKeys:    pick(q, "CorrectKeys", "correctKeys") || [],
});

export default function QuestionsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showRetired, setShowRetired] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const debounced = useDebouncedValue(search, 300);
    const once = useSingleFlight();

    const close = useCallback(() => setEditing(null), []);
    useBackCloses(!!editing, close);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setRows(((await listQuestions({ search: debounced, includeRetired: true })) || []).map(fromApi));
        } catch (err) {
            toast.error(errorText(err, "Could not load the questions."));
        } finally {
            setLoading(false);
        }
    }, [debounced]);

    useEffect(() => { load(); }, [load]);

    const visible = useMemo(() => rows.filter((r) => showRetired || r.isActive), [rows, showRetired]);
    const domains = useMemo(() => [...new Set(rows.map((r) => r.domain))].sort(), [rows]);
    const activeCount = rows.filter((r) => r.isActive).length;

    const store = (saved, isNew) => setRows((list) => (isNew
        ? [...list, saved].sort((a, b) => a.questionCode.localeCompare(b.questionCode))
        : list.map((x) => (x.questionId === saved.questionId ? saved : x))));

    const save = (q) => once(`save:${q.questionId ?? "new"}`, async () => {
        const blankOption = (q.options || []).find((o) => !String(o.text || "").trim());
        if (blankOption) { toast.error(`Enter the text of option ${blankOption.key}.`); return; }
        if (!String(q.questionText || "").trim()) { toast.error("Enter the question text."); return; }
        setSaving(true);
        try {
            const saved = fromApi(q.questionId ? await updateQuestion(q.questionId, q) : await createQuestion(q));
            store(saved, !q.questionId);
            toast.success(`Question ${saved.questionCode} saved.`);
            setEditing(null);
        } catch (err) {
            toast.error(errorText(err, "The question was not saved."));
        } finally {
            setSaving(false);
        }
    });

    // Retire / Restore from the list: its own request (never closes an editor that is open), and it
    // sends the row as it is in the list right now, so a newer edit is not overwritten.
    const [toggling, setToggling] = useState(null);
    const setActive = (q, isActive) => once(`active:${q.questionId}`, async () => {
        setToggling(q.questionId);
        try {
            const current = rows.find((x) => x.questionId === q.questionId) || q;
            store(fromApi(await updateQuestion(q.questionId, { ...current, isActive })), false);
            // Keep an open editor of the same question in step, so saving it does not undo this.
            setEditing((e) => (e && e.questionId === q.questionId ? { ...e, isActive } : e));
            toast.success(`Question ${q.questionCode} ${isActive ? "restored" : "retired"}.`);
        } catch (err) {
            toast.error(errorText(err, "The change was not saved."));
        } finally {
            setToggling(null);
        }
    });

    return (
        <>
            <Row className="g-2 align-items-center mb-3">
                <Col md={5}>
                    <Input placeholder="Search code, domain, text or source" value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Col md={4}>
                    <FormGroup switch className="mb-0">
                        <Input type="switch" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
                        <Label check>Show retired questions</Label>
                    </FormGroup>
                </Col>
                <Col md={3} className="text-md-end">
                    <Button color="primary" onClick={() => setEditing(blank())}><i className="ri-add-line me-1" />Add question</Button>
                </Col>
            </Row>
            <div className="small text-muted mb-2">{activeCount} active question{activeCount === 1 ? "" : "s"} in the bank.</div>

            {loading ? (
                <div className="text-center py-5"><Spinner size="sm" /> Loading…</div>
            ) : visible.length === 0 ? (
                <EmptyState title="No questions"
                    message={search ? "No question matches the search."
                        : rows.length > 0 ? "Every question is retired. Turn on \"Show retired questions\" to restore some."
                        : "The bank is empty."} />
            ) : (
                <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Code</th>
                                <th>Domain</th>
                                <th style={{ width: "45%" }}>Question</th>
                                <th>Type</th>
                                <th>Answer</th>
                                <th className="text-end" />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((q) => (
                                <tr key={q.questionId} className={q.isActive ? "" : "text-muted"}>
                                    <td className="text-nowrap">
                                        {q.questionCode}
                                        {q.isCritical && <Badge color="danger" className="ms-1">Critical</Badge>}
                                        {!q.isActive && <Badge color="light" className="text-muted ms-1">retired</Badge>}
                                    </td>
                                    <td className="small">{q.domain}</td>
                                    <td className="small">{q.questionText}</td>
                                    <td className="small">{q.questionType === "multi" ? "Select all" : "Single"}</td>
                                    <td className="small fw-semibold">{q.correctKeys.join(", ")}</td>
                                    <td className="text-end text-nowrap">
                                        <Button size="sm" color="light" className="me-1" onClick={() => setEditing({ ...q })}>Edit</Button>
                                        <Button size="sm" color={q.isActive ? "light" : "soft-success"} disabled={toggling === q.questionId} onClick={() => setActive(q, !q.isActive)}>
                                            {q.isActive ? "Retire" : "Restore"}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {editing && <QuestionEditor value={editing} domains={domains} saving={saving} onCancel={close} onSave={save} />}
        </>
    );
}

function QuestionEditor({ value, domains, saving, onCancel, onSave }) {
    const [q, setQ] = useState(value);
    const set = (patch) => setQ((x) => ({ ...x, ...patch }));
    const roles = q.roles.split(",").map((r) => r.trim()).filter(Boolean);

    const toggleRole = (r) => {
        const next = roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];
        set({ roles: next.join(",") });
    };
    const toggleCorrect = (k) => {
        if (q.questionType === "single") return set({ correctKeys: [k] });
        set({ correctKeys: q.correctKeys.includes(k) ? q.correctKeys.filter((x) => x !== k) : [...q.correctKeys, k].sort() });
    };

    return (
        <Modal isOpen toggle={onCancel} size="lg" centered scrollable>
            <ModalHeader toggle={onCancel}>{q.questionId ? `Edit ${q.questionCode}` : "Add question"}</ModalHeader>
            <ModalBody>
                <Form onSubmit={(e) => { e.preventDefault(); onSave(q); }}>
                    <Row className="g-2">
                        <Col md={3}>
                            <Label className="form-label">Code</Label>
                            <Input value={q.questionCode} maxLength={20} placeholder="DC-016" onChange={(e) => set({ questionCode: e.target.value })} />
                        </Col>
                        <Col md={5}>
                            <Label className="form-label">Domain</Label>
                            <Input value={q.domain} list="cq-domains" maxLength={120} onChange={(e) => set({ domain: e.target.value })} />
                            <datalist id="cq-domains">{domains.map((d) => <option key={d} value={d} />)}</datalist>
                        </Col>
                        <Col md={4}>
                            <Label className="form-label">Type</Label>
                            <Input type="select" value={q.questionType}
                                onChange={(e) => set({ questionType: e.target.value, correctKeys: e.target.value === "single" ? q.correctKeys.slice(0, 1) : q.correctKeys })}>
                                <option value="single">Single answer</option>
                                <option value="multi">Select all that apply</option>
                            </Input>
                        </Col>
                        <Col xs={12}>
                            <Label className="form-label">Question</Label>
                            <Input type="textarea" rows={3} value={q.questionText} onChange={(e) => set({ questionText: e.target.value })} />
                        </Col>
                        <Col xs={12}>
                            <Label className="form-label mb-1">Options <span className="small text-muted">(tick the correct {q.questionType === "multi" ? "options" : "option"})</span></Label>
                            {q.options.map((o, i) => (
                                <div key={o.key} className="d-flex align-items-center gap-2 mb-2">
                                    <Input
                                        type={q.questionType === "multi" ? "checkbox" : "radio"}
                                        name="cq-correct"
                                        checked={q.correctKeys.includes(o.key)}
                                        onChange={() => toggleCorrect(o.key)}
                                        title="Correct answer"
                                    />
                                    <b style={{ width: 16 }}>{o.key}</b>
                                    <Input value={o.text} maxLength={1000}
                                        onChange={(e) => set({ options: q.options.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
                                </div>
                            ))}
                        </Col>
                        <Col xs={12}>
                            <Label className="form-label">Rationale <span className="small text-muted">(shown with the result)</span></Label>
                            <Input type="textarea" rows={2} value={q.rationale} onChange={(e) => set({ rationale: e.target.value })} />
                        </Col>
                        <Col md={6}>
                            <Label className="form-label">Source document</Label>
                            <Input value={q.sourceDocument} maxLength={200} placeholder="SOP-0028 Rev 13" onChange={(e) => set({ sourceDocument: e.target.value })} />
                        </Col>
                        <Col md={6}>
                            <Label className="form-label">Section</Label>
                            <Input value={q.sourceSection} maxLength={200} onChange={(e) => set({ sourceSection: e.target.value })} />
                        </Col>
                        <Col md={6}>
                            <Label className="form-label d-block">Roles</Label>
                            {ROLE_CHOICES.map((r) => (
                                <FormGroup check inline key={r}>
                                    <Input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} />
                                    <Label check>{r}</Label>
                                </FormGroup>
                            ))}
                        </Col>
                        <Col md={3}>
                            <Label className="form-label">Difficulty</Label>
                            <Input type="select" value={q.difficulty} onChange={(e) => set({ difficulty: e.target.value })}>
                                <option value="">—</option>
                                <option>Foundation</option>
                                <option>Intermediate</option>
                                <option>Advanced</option>
                            </Input>
                        </Col>
                        <Col md={3} className="d-flex flex-column justify-content-end">
                            <FormGroup check>
                                <Input type="checkbox" checked={q.isCritical} onChange={(e) => set({ isCritical: e.target.checked })} />
                                <Label check>Critical</Label>
                            </FormGroup>
                            <FormGroup check>
                                <Input type="checkbox" checked={q.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
                                <Label check>Active (drawn in quizzes)</Label>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={onCancel}>Cancel</Button>
                <Button color="primary" disabled={saving} onClick={() => onSave(q)}>
                    {saving ? <Spinner size="sm" className="me-1" /> : null}Save
                </Button>
            </ModalFooter>
        </Modal>
    );
}
